import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { PageShell } from '@/components/PageShell';
import { PageSkeleton } from '@/components/PageSkeleton';
import { PageHeader } from '@/components/PageHeader';
import { toast } from 'sonner';
import { Check, Loader2, Mail, Briefcase, MapPin, Building2, Lock, Eye, EyeOff, User, ShieldAlert, Shield, CheckCircle, Activity } from 'lucide-react';
import api from '@/lib/api';
import { AVATAR_OPTIONS, isSafeAvatar, toAvatarValue } from '@/lib/avatarCatalog';
import { UserAvatar } from '@/components/UserAvatar';

const ROLE_BADGE_CLASS = 'bg-primary/10 text-primary';

const ROLE_CONFIG = {
  admin: { label: 'Admin', color: ROLE_BADGE_CLASS, icon: Shield },
  approver: { label: 'Approver', color: ROLE_BADGE_CLASS, icon: CheckCircle },
  analyst: { label: 'Analyst', color: ROLE_BADGE_CLASS, icon: Activity },
  viewer: { label: 'Viewer', color: ROLE_BADGE_CLASS, icon: Eye },
  employee: { label: 'Employee', color: ROLE_BADGE_CLASS, icon: User },
};

function RoleBadge({ role }) {
  const normalizedRole = (role || 'viewer').toLowerCase();
  const config = ROLE_CONFIG[normalizedRole] || {
    label: normalizedRole.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()),
    color: ROLE_BADGE_CLASS,
    icon: User,
  };
  const Icon = config.icon;

  return (
    <Badge className={`mt-1 inline-flex items-center gap-1 ${config.color}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

export default function ProfileSettingsPage() {
  const { user, refreshUser, updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [countries, setCountries] = useState([]);
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    email: '',
    job_title: '',
    department: '',
    country: '',
    avatar_url: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [selectedAvatar, setSelectedAvatar] = useState('');

  useEffect(() => {
    fetchProfile();
    fetchCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCountries = async () => {
    try {
      const res = await api.get('/settings/countries');
      setCountries(res.data || []);
    } catch (err) {
      console.error('Failed to fetch countries:', err);
      // Fallback to common countries
      setCountries([
        { code: 'US', name: 'United States' },
        { code: 'GB', name: 'United Kingdom' },
        { code: 'CA', name: 'Canada' },
        { code: 'AU', name: 'Australia' },
        { code: 'DE', name: 'Germany' },
        { code: 'FR', name: 'France' },
        { code: 'IN', name: 'India' },
        { code: 'JP', name: 'Japan' },
        { code: 'SG', name: 'Singapore' },
        { code: 'AE', name: 'United Arab Emirates' },
      ]);
    }
  };

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await api.get('/auth/profile');
      const data = res.data;
      setProfile({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        email: data.email || user?.email || '',
        job_title: data.job_title || '',
        department: data.department || '',
        country: data.country || '',
        avatar_url: data.avatar_url || '',
      });
      setSelectedAvatar(isSafeAvatar(data.avatar_url) ? data.avatar_url : '');
    } catch (_err) {
      // If profile endpoint doesn't exist, use auth user data
      setProfile({
        first_name: user?.first_name || user?.name?.split(' ')[0] || '',
        last_name: user?.last_name || user?.name?.split(' ').slice(1).join(' ') || '',
        email: user?.email || '',
        job_title: user?.job_title || '',
        department: user?.department || '',
        country: user?.country || '',
        avatar_url: user?.avatar_url || '',
      });
      setSelectedAvatar(isSafeAvatar(user?.avatar_url) ? user.avatar_url : '');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!profile.first_name.trim() || !profile.last_name.trim()) {
      toast.error('First name and last name are required');
      return;
    }
    if (!profile.country.trim()) {
      toast.error('Country is required');
      return;
    }

    setSaving(true);
    try {
      await api.put('/auth/profile', {
        ...profile,
        avatar_url: selectedAvatar,
      });
      toast.success('Profile updated successfully');
      if (refreshUser) {
        await refreshUser();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password) {
      toast.error('Current password is required');
      return;
    }
    if (!passwordForm.new_password) {
      toast.error('New password is required');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    setChangingPassword(true);
    try {
      const res = await api.post('/auth/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      toast.success('Password changed successfully');
      // Swap to the reissued token: the login token still carries the
      // force_password_change claim and 403s on every endpoint (EIP-154).
      if (res.data?.access_token) {
        localStorage.setItem('eva_token', res.data.access_token);
      }
      if (res.data?.force_password_change === false) {
        updateUser?.({ force_password_change: false });
      }
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const displayName = `${profile.first_name} ${profile.last_name}`.trim() || user?.email?.split('@')[0] || 'User';

  if (loading) {
    return (
      <PageSkeleton
        maxWidth="max-w-3xl"
        kpis={0}
        sections={3}
        sectionClassName="h-64"
        data-testid="profile-settings-loading"
      />
    );
  }

  return (
    <PageShell maxWidth="max-w-3xl" data-testid="profile-settings-page">
      <PageHeader
        title="Profile Settings"
        icon={User}
        description="Manage your personal information and avatar"
      />

      {user?.force_password_change && (
        <Card className="border-amber-300 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/30">
          <CardContent className="flex items-start gap-3 p-4">
            <ShieldAlert className="mt-0.5 h-5 w-5 text-amber-600 dark:text-amber-400" />
            <div>
              <p className="font-semibold text-amber-950 dark:text-amber-100">Password change required</p>
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Your administrator created a temporary password. Update it before continuing.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Avatar Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Avatar</CardTitle>
          <CardDescription>Choose an avatar that represents you</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6 mb-6">
            <div className="relative">
              <UserAvatar
                user={{ ...profile, name: displayName, avatar_url: selectedAvatar }}
                size="xl"
                className="border-4 border-primary/20"
              />
            </div>
            <div>
              <p className="font-semibold text-foreground text-lg">{displayName}</p>
              <p className="text-sm text-muted-foreground">{profile.email}</p>
              <RoleBadge role={user?.platform_role} />
            </div>
          </div>

          <Label className="mb-3 block">Select an Avatar</Label>
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
            {AVATAR_OPTIONS.map((avatar) => {
              const Icon = avatar.Icon;
              const avatarValue = toAvatarValue(avatar.id);
              const selected = selectedAvatar === avatarValue;
              return (
              <button
                key={avatar.id}
                type="button"
                aria-label={`Use ${avatar.label} avatar`}
                title={avatar.label}
                onClick={() => setSelectedAvatar(avatarValue)}
                className={`relative w-12 h-12 rounded-full border-2 transition-all hover:scale-110 flex items-center justify-center ${avatar.bgClass} ${avatar.textClass} ${
                  selected
                    ? 'border-primary ring-2 ring-primary ring-offset-2' 
                    : 'border-transparent hover:border-muted-foreground/30'
                }`}
              >
                <Icon className="w-6 h-6" />
                {selected && (
                  <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                    <Check className="w-4 h-4 text-primary" />
                  </div>
                )}
              </button>
              );
            })}
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="mt-3 text-muted-foreground"
            onClick={() => setSelectedAvatar('')}
          >
            Use assigned default
          </Button>
        </CardContent>
      </Card>

      {/* Personal Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Personal Information</CardTitle>
          <CardDescription>Update your personal details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-1">
                First Name <span className="text-destructive">*</span>
              </Label>
              <Input
                value={profile.first_name}
                onChange={(e) => setProfile(prev => ({ ...prev, first_name: e.target.value }))}
                placeholder="John"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1">
                Last Name <span className="text-destructive">*</span>
              </Label>
              <Input
                value={profile.last_name}
                onChange={(e) => setProfile(prev => ({ ...prev, last_name: e.target.value }))}
                placeholder="Smith"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-1">
              <Mail className="w-4 h-4" /> Email
            </Label>
            <Input
              value={profile.email}
              disabled
              className="mt-1 bg-muted"
            />
            <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
          </div>

          <div>
            <Label className="flex items-center gap-1">
              <Briefcase className="w-4 h-4" /> Job Title
            </Label>
            <Input
              value={profile.job_title}
              onChange={(e) => setProfile(prev => ({ ...prev, job_title: e.target.value }))}
              placeholder="HR Manager, Benefits Coordinator, Recruiter..."
              className="mt-1"
            />
          </div>

          <div>
            <Label className="flex items-center gap-1">
              <Building2 className="w-4 h-4" /> Department
            </Label>
            <Input
              value={profile.department}
              onChange={(e) => setProfile(prev => ({ ...prev, department: e.target.value }))}
              placeholder="Human Resources, People Operations, Talent..."
              className="mt-1"
            />
          </div>

          <div>
            <Label className="flex items-center gap-1">
              <MapPin className="w-4 h-4" /> Country <span className="text-destructive">*</span>
            </Label>
            <Select
              value={profile.country}
              onValueChange={(value) => setProfile(prev => ({ ...prev, country: value }))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select a country" />
              </SelectTrigger>
              <SelectContent>
                {countries.map((country) => (
                  <SelectItem key={country.code} value={country.name}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Password Change */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Change Password
          </CardTitle>
          <CardDescription>Update your account password</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Current Password</Label>
            <div className="relative mt-1">
              <Input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                placeholder="Enter current password"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
              >
                {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <div>
            <Label>New Password</Label>
            <div className="relative mt-1">
              <Input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                placeholder="Enter new password (min 8 characters)"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
              >
                {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <div>
            <Label>Confirm New Password</Label>
            <div className="relative mt-1">
              <Input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                placeholder="Confirm new password"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
              >
                {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <Button 
            onClick={handleChangePassword} 
            disabled={changingPassword}
            variant="outline"
            data-testid="change-password-btn"
          >
            {changingPassword && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Change Password
          </Button>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="save-profile-btn"
        >
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Save Changes
        </Button>
      </div>
    </PageShell>
  );
}
